/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { VAT_PATTERNS } from '@/constants/vatPatterns';
import { IntlShape } from 'react-intl';
import { DiscountType } from '@/types/promo-management.types';
import {
  BerthType,
  CouponEligibilityRule,
  Seasons,
  HarborBookingType,
} from '@/constant';

const transformLocalizedNumber = (value: unknown, originalValue: unknown) => {
  if (
    originalValue === '' ||
    originalValue === null ||
    originalValue === undefined
  ) {
    return undefined;
  }

  if (typeof originalValue === 'string') {
    const normalized = originalValue.replace(/\s/g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? NaN : parsed;
  }

  return value;
};

const FI_BUSINESS_ID_PATTERN = /^\d{7}-\d$/;
const FI_BUSINESS_ID_WEIGHTS = [7, 9, 10, 5, 8, 4, 2];
const NON_FI_BUSINESS_ID_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9-]{5,20}$/;

export const getPostcodePattern = (country: string): RegExp => {
  switch ((country || 'FI').toUpperCase()) {
    case 'IN':
      return /^\d{6}$/;
    case 'US':
      return /^\d{5}(-\d{4})?$/;
    case 'GB':
      return /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
    case 'FI':
      return /^\d{5}$/;
    case 'AT':
      return /^\d{4}$/;
    default:
      return /^[A-Za-z0-9\s-]{3,10}$/;
  }
};

export const isValidFinnishBusinessId = (value: string): boolean => {
  const trimmed = value.trim();
  if (!FI_BUSINESS_ID_PATTERN.test(trimmed)) return false;

  const [body, checkDigit] = trimmed.split('-');
  const sum = body
    .split('')
    .reduce(
      (total, digit, index) =>
        total + Number(digit) * FI_BUSINESS_ID_WEIGHTS[index],
      0
    );
  const remainder = sum % 11;
  if (remainder === 1) return false;

  const expected = remainder === 0 ? 0 : 11 - remainder;
  return expected === Number(checkDigit);
};

export const isValidBusinessId = (
  value: string,
  country?: string
): boolean => {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;

  const countryCode = (country || 'FI').trim().toUpperCase();
  if (countryCode === 'FI') {
    return isValidFinnishBusinessId(trimmed);
  }

  return NON_FI_BUSINESS_ID_PATTERN.test(trimmed);
};

export const businessIdValidation = (
  isRequired: boolean = true,
  countryShortCodeField: string = 'country_short_code'
) => {
  let validator = Yup.string().test(
    'business-id-format',
    'Invalid Business ID',
    function (value) {
      if (!value || value.trim() === '') return !isRequired;

      const country =
        this.parent?.[countryShortCodeField] ||
        this.parent?.country_short_code ||
        this.parent?.country ||
        'FI';

      return isValidBusinessId(value, country);
    }
  );

  if (isRequired) {
    validator = validator.required('Business ID is required');
  }

  return validator;
};

export const postcodeValidation = (
  isRequired: boolean = true,
  countryShortCodeField: string = 'country_short_code'
) => {
  let validator = Yup.string().test(
    'postal-format',
    'Invalid postal code format',
    function (value) {
      if (!value || value.trim() === '') return !isRequired;

      const country =
        this.parent?.[countryShortCodeField] ||
        this.parent?.country_short_code ||
        this.parent?.country ||
        'FI';

      return getPostcodePattern(country).test(String(value).trim());
    }
  );

  if (isRequired) {
    validator = validator.required('Postal code is required');
  }

  return validator;
};

export const vatIdValidation = (countryCode: string, intl?: IntlShape) => {
  return Yup.string()
    .test(
      'vat-format',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.INVALID_VAT' })
        : 'Invalid VAT ID format for the selected country',
      function (value) {
        if (!value) return true;
        if (!countryCode) return true;

        const country = countryCode.toUpperCase();
        const vatPattern = VAT_PATTERNS[country];

        if (vatPattern) {
          const cleanVat = value.replace(/[^A-Z0-9]/gi, '');
          const isValid = vatPattern.pattern.test(cleanVat);

          if (!isValid) {
            return this.createError({
              message: `Invalid VAT ID for ${country}. Expected format: ${vatPattern.description} (e.g., ${vatPattern.example})`,
            });
          }
          return true;
        }

        return /^[A-Z0-9\-\s]{4,20}$/i.test(value);
      }
    )
    .transform((value) => {
      if (!value) return value;
      return value.toString().replace(/\s+/g, '').toUpperCase();
    });
};

export const textFieldValidation = (
  fieldName: string,
  minLength: number = 2,
  maxLength: number = 50,
  isRequired: boolean = true,
  intl?: IntlShape
) => {
  let validator = Yup.string()
    .test(
      'no-leading-space',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
        : 'Cannot start with spaces',
      (value) => {
        if (!value) return true;
        return !value.startsWith(' ');
      }
    )
    .min(
      minLength,
      intl
        ? intl.formatMessage(
            { id: `VALIDATION.MIN_LENGTH` },
            { field: fieldName, count: minLength }
          )
        : `${fieldName} must be at least ${minLength} characters`
    )
    .max(
      maxLength,
      intl
        ? intl.formatMessage(
            { id: `VALIDATION.MAX_LENGTH` },
            { field: fieldName, count: maxLength }
          )
        : `${fieldName} cannot exceed ${maxLength} characters`
    );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage(
            { id: `VALIDATION.REQUIRED` },
            { field: fieldName }
          )
        : `${fieldName} is required`
    );
  }

  return validator;
};

export const numberFieldValidation = (
  fieldName: string,
  min?: number,
  max?: number,
  isRequired: boolean = true,
  intl?: IntlShape
) => {
  let validator = Yup.number()
    .transform(transformLocalizedNumber)
    .typeError(
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.MUST_BE_NUMBER' },
            { field: fieldName }
          )
        : `${fieldName} must be a number`
    )
    .test(
      'no-leading-space',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
        : 'Cannot start with spaces',
      (value) => {
        if (value === undefined || value === null) return true;
        return !String(value).startsWith(' ');
      }
    );

  if (min !== undefined) {
    validator = validator.min(
      min,
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.MIN_VALUE' },
            { field: fieldName, min }
          )
        : `${fieldName} must be at least ${min}`
    );
  }

  if (max !== undefined) {
    validator = validator.max(
      max,
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.MAX_VALUE' },
            { field: fieldName, max }
          )
        : `${fieldName} cannot exceed ${max}`
    );
  }

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: fieldName }
          )
        : `${fieldName} is required`
    );
  }

  return validator;
};

export const isValidCanonicalPhone = (
  value?: string,
  isoCountry?: string,
  callingCode?: string
): boolean => {
  if (!value || value.trim() === '') return false;
  const cleaned = value.replace(/[^\d+]/g, '');
  try {
    if (cleaned.startsWith('+')) {
      return parsePhoneNumberFromString(cleaned)?.isValid() ?? false;
    }
    if (callingCode) {
      const dial = callingCode.startsWith('+')
        ? callingCode
        : `+${callingCode}`;
      const withDial = parsePhoneNumberFromString(`${dial}${cleaned}`);
      if (withDial?.isValid()) return true;
    }
    if (isoCountry) {
      return (
        parsePhoneNumberFromString(cleaned, isoCountry as any)?.isValid() ??
        false
      );
    }
  } catch {
    return false;
  }
  return false;
};

export const phoneNumberValidation = (
  fieldName: string,
  isRequired: boolean = true,
  countryShortCodeField: string = 'countryShortCode',
  intl?: IntlShape
) => {
  let validator = Yup.string().test(
    'is-valid-phone',
    intl
      ? intl.formatMessage(
          { id: 'VALIDATION.INVALID_PHONE_NUMBER' },
          { field: fieldName }
        )
      : `${fieldName} is not a valid mobile number`,
    function (value) {
      const countryShortCode =
        this.parent?.[countryShortCodeField] ||
        this.parent?.phoneCountryShortCode ||
        this.parent?.phone_country_short_code ||
        this.parent?.contact_phone_country_short_code ||
        this.parent?.countryShortCode ||
        this.parent?.country_short_code ||
        '';
      const callingCode =
        this.parent?.countryCode || this.parent?.country_code || '';
      if (!value || value.trim() === '') return !isRequired;
      if (!countryShortCode && !callingCode) return !isRequired;
      return isValidCanonicalPhone(value, countryShortCode, callingCode);
    }
  );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: fieldName }
          )
        : `${fieldName} is required`
    );
  }

  return validator;
};

export const emailValidation = (
  isRequired: boolean = true,
  intl?: IntlShape
) => {
  let validator = Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .test(
      'no-spaces',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.EMAIL_NO_SPACES' })
        : 'Email cannot contain spaces',
      (value) => {
        if (!value) return true;
        return !value.includes(' ');
      }
    )
    .test(
      'optional-email',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.EMAIL_INVALID_FORMAT' })
        : 'Invalid email format',
      (value) => {
        if (!value) return true;
        const base = Yup.string()
          .email('Invalid email address')
          .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            'Invalid email format'
          )
          .test(
            'email',
            intl
              ? intl.formatMessage({ id: 'VALIDATION.EMAIL_NO_START_END_DOT' })
              : 'Email cannot start or end with a dot',
            (v) => {
              if (!v) return true;
              return !v.startsWith('.') && !v.endsWith('.');
            }
          )
          .test(
            'email',
            intl
              ? intl.formatMessage({
                  id: 'VALIDATION.EMAIL_NO_CONSECUTIVE_DOTS',
                })
              : 'Email cannot contain consecutive dots',
            (v) => {
              if (!v) return true;
              return !v.includes('..');
            }
          )
          .test(
            'email',
            intl
              ? intl.formatMessage({ id: 'VALIDATION.EMAIL_LOCAL_TOO_LONG' })
              : 'Local part cannot exceed 64 characters',
            (v) => {
              if (!v) return true;
              const localPart = v.split('@')[0];
              return localPart.length <= 64;
            }
          )
          .test(
            'email',
            intl
              ? intl.formatMessage({ id: 'VALIDATION.EMAIL_DOMAIN_TOO_LONG' })
              : 'Domain part cannot exceed 255 characters',
            (v) => {
              if (!v) return true;
              const domainPart = v.split('@')[1];
              return domainPart?.length <= 255;
            }
          )
          .max(
            320,
            intl
              ? intl.formatMessage({ id: 'VALIDATION.EMAIL_TOO_LONG' })
              : 'Email cannot exceed 320 characters'
          );
        return base.isValidSync(value);
      }
    );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.EMAIL_REQUIRED' })
        : 'Email is required'
    );
  }

  return validator;
};

export const passwordValidation = (
  isRequired: boolean = true,
  noMatch?: boolean,
  intl?: IntlShape
) => {
  let validator = Yup.string()
    .test(
      'no-leading-space',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
        : 'Cannot start with spaces',
      (value) => {
        if (!value) return true;
        return !value.startsWith(' ');
      }
    )
    .min(
      8,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_MIN_LENGTH' })
        : 'Password must be at least 8 characters'
    )
    .max(
      50,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_MAX_LENGTH' })
        : 'Password cannot exceed 50 characters'
    );

  if (noMatch === undefined || noMatch) {
    validator = validator.matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,}$/,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_COMPLEXITY' })
        : 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    );
  }

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' })
        : 'Password is required'
    );
  }

  return validator;
};

export const NEW_PASSWORD_MIN_LENGTH = 12;
export const NEW_PASSWORD_MAX_LENGTH = 128;

/** Login only: required + max length per policy; no min/complexity (existing passwords). */
export const loginPasswordValidation = (intl?: IntlShape) => {
  return Yup.string()
    .max(
      NEW_PASSWORD_MAX_LENGTH,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_MAX_LENGTH' })
        : 'Password cannot exceed 128 characters'
    )
    .required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' })
        : 'Password is required'
    );
};

/** Beta policy: length 12–128 plus upper, lower, digit, special. Not for login. */
export const NEW_PASSWORD_COMPLEXITY_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/;

export const newPasswordValidation = (
  isRequired: boolean = true,
  intl?: IntlShape
) => {
  let validator = Yup.string()
    .test(
      'no-leading-space',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
        : 'Cannot start with spaces',
      (value) => {
        if (!value) return true;
        return !value.startsWith(' ');
      }
    )
    .min(
      NEW_PASSWORD_MIN_LENGTH,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NEW_PASSWORD_MIN_LENGTH' })
        : 'Password must be at least 12 characters'
    )
    .max(
      NEW_PASSWORD_MAX_LENGTH,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NEW_PASSWORD_MAX_LENGTH' })
        : 'Password cannot exceed 128 characters'
    )
    .matches(
      NEW_PASSWORD_COMPLEXITY_REGEX,
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NEW_PASSWORD_COMPLEXITY' })
        : 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' })
        : 'Password is required'
    );
  }

  return validator;
};

/** Client-side check for forms without Yup (e.g. DMO register). Returns error message or null. */
export const getNewPasswordValidationError = (
  password: string
): string | null => {
  const trimmed = password.trim();
  if (!trimmed) return 'Password is required';
  if (trimmed.length < NEW_PASSWORD_MIN_LENGTH) {
    return 'Password must be at least 12 characters';
  }
  if (trimmed.length > NEW_PASSWORD_MAX_LENGTH) {
    return 'Password cannot exceed 128 characters';
  }
  if (!NEW_PASSWORD_COMPLEXITY_REGEX.test(trimmed)) {
    return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
  }
  return null;
};

export const confirmPasswordValidation = (
  isRequired: boolean = true,
  intl?: IntlShape
) => {
  let validator = Yup.string()
    .oneOf(
      [Yup.ref('newPassword')],
      intl
        ? intl.formatMessage({ id: 'VALIDATION.PASSWORDS_MUST_MATCH' })
        : 'Passwords must match'
    )
    .test(
      'no-leading-space',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
        : 'Cannot start with spaces',
      (value) => {
        if (!value) return true;
        return !value.startsWith(' ');
      }
    );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.CONFIRM_PASSWORD_REQUIRED' })
        : 'Confirm password is required'
    );
  }

  return validator;
};

export const resetPasswordChangeSchema = (intl?: IntlShape) =>
  Yup.object().shape({
    newPassword: newPasswordValidation(true, intl),
    confirmPassword: confirmPasswordValidation(true, intl),
  });

export const changePasswordSchema = (intl?: IntlShape) =>
  Yup.object().shape({
    oldPassword: Yup.string()
      .test(
        'no-leading-space',
        intl
          ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
          : 'Cannot start with spaces',
        (value) => {
          if (!value) return true;
          return !value.startsWith(' ');
        }
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' })
          : 'Password is required'
      ),
    newPassword: newPasswordValidation(true, intl).test(
      'different-from-old',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.NEW_PASSWORD_DIFFERENT' })
        : 'New password must be different from the current password',
      function (value) {
        return value !== this.parent.oldPassword;
      }
    ),
    confirmPassword: confirmPasswordValidation(true, intl),
  });

export const imageValidation = (
  isRequired: boolean = false,
  maxSizeMB: number = 2,
  minSizeKB: number = 0,
  type: 'logo' | 'fairway_map' | 'fairway_entrance_map' | 'media' = 'logo',
  intl?: IntlShape
) => {
  let minKB = 0;
  if (type === 'logo') minKB = 5;
  else if (type === 'fairway_map' || type === 'fairway_entrance_map')
    minKB = 10;
  else if (type === 'media') minKB = 50;
  if (minSizeKB > 0) minKB = minSizeKB;
  const minSizeBytes = minKB * 1024;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/svg+xml',
  ];

  let validator = Yup.mixed<File>()
    .nullable()
    .test(
      'fileType',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.IMAGE_INVALID_TYPE' })
        : 'Only JPG, JPEG, PNG, and SVG files are allowed',
      (value) => {
        if (!value) return true; // Skip if no file
        return allowedTypes.includes(value.type);
      }
    )
    .test(
      'fileSize',
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.IMAGE_INVALID_SIZE' },
            { minKB: minKB, maxMB: maxSizeMB }
          )
        : `File size must be between ${minKB}KB and ${maxSizeMB}MB`,
      (value) => {
        if (!value) return true; // Skip if no file
        return value.size >= minSizeBytes && value.size <= maxSizeBytes;
      }
    );

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.IMAGE_REQUIRED' })
        : 'Image is required'
    );
  }

  return validator;
};

export const businessHoursValidation = (intl?: IntlShape) =>
  Yup.string()
    .notRequired()
    .nullable()
    .test(
      'valid-hours',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.INVALID_BUSINESS_HOURS' })
        : 'Invalid business hours format or close time must be after open time for open days',
      (value) => {
        if (!value) return true;

        try {
          const hours = JSON.parse(value) as Record<
            string,
            { open?: string; close?: string }
          >;

          const validDays = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
          ];

          for (const day of Object.keys(hours)) {
            if (!validDays.includes(day)) {
              return false;
            }
          }

          for (const day of validDays) {
            if (!(day in hours)) {
              continue;
            }

            const dayHours = hours[day];

            if (!('open' in dayHours) || !('close' in dayHours)) {
              return false;
            }

            const { open, close } = dayHours;

            const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
            if (!timeRegex.test(open!) || !timeRegex.test(close!)) {
              return false;
            }

            const openTime = new Date(`2000-01-01 ${open}`);
            const closeTime = new Date(`2000-01-01 ${close}`);

            if (isNaN(openTime.getTime()) || isNaN(closeTime.getTime())) {
              return false;
            }

            if (closeTime <= openTime) {
              return false;
            }
          }

          return true;
        } catch (error) {
          console.error(error);
          return false;
        }
      }
    );

export const mediaValidation = (
  isRequired: boolean = false,
  maxImageSizeMB: number = 2,
  maxVideoSizeMB: number = 10,
  minImageSizeKB: number = 50,
  minVideoSizeMB: number = 1,
  intl?: IntlShape
) => {
  const maxImageSizeBytes = maxImageSizeMB * 1024 * 1024;
  const maxVideoSizeBytes = maxVideoSizeMB * 1024 * 1024;
  const minImageSizeBytes = minImageSizeKB * 1024;
  const minVideoSizeBytes = minVideoSizeMB * 1024 * 1024;
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4'];

  let validator = Yup.mixed<File | string>()
    .nullable()
    .test(
      'fileType',
      intl
        ? intl.formatMessage({ id: 'VALIDATION.MEDIA_INVALID_TYPE' })
        : 'Only JPG, JPEG, PNG, and MP4 files are allowed',
      (value) => {
        if (!value || typeof value === 'string') return true;
        return allowedTypes.includes(value.type);
      }
    )
    .test('fileSize', (value, context) => {
      if (!value || typeof value === 'string') return true;
      if (value.type.startsWith('image/')) {
        return value.size >= minImageSizeBytes &&
          value.size <= maxImageSizeBytes
          ? true
          : context.createError({
              message: `File size must be between ${minImageSizeKB}KB and ${maxImageSizeMB}MB`,
            });
      } else if (value.type.startsWith('video/')) {
        return value.size >= minVideoSizeBytes &&
          value.size <= maxVideoSizeBytes
          ? true
          : context.createError({
              message: `File size must be between ${minVideoSizeMB}MB and ${maxVideoSizeMB}MB`,
            });
      }
      return true;
    });

  if (isRequired) {
    validator = validator.required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.MEDIA_REQUIRED' })
        : 'Media is required'
    );
  }

  return validator;
};

export const decimalFieldValidation = (
  fieldName: string,
  min: number,
  max: number,
  required: boolean,
  unit?: string,
  maxDecimals: number = 2,
  intl?: IntlShape
) => {
  const validator = Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .min(
      min,
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.DECIMAL_MIN_VALUE' },
            { field: fieldName, min, unit: unit || '' }
          )
        : `${fieldName} must be at least ${min}${unit ? ` ${unit}` : ''}`
    )
    .max(
      max,
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.DECIMAL_MAX_VALUE' },
            { field: fieldName, max, unit: unit || '' }
          )
        : `${fieldName} must not exceed ${max}${unit ? ` ${unit}` : ''}`
    )
    .test(
      'is-decimal',
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.DECIMAL_MAX_DECIMALS' },
            { field: fieldName, maxDecimals }
          )
        : `${fieldName} must have up to ${maxDecimals} decimal places`,
      (value) =>
        value === undefined ||
        value === null ||
        Number(value.toFixed(maxDecimals)) === value ||
        new RegExp(`^\\d+(\\.\\d{1,${maxDecimals}})?$`).test(value.toString())
    );

  if (required) {
    return validator.required(
      intl
        ? intl.formatMessage(
            { id: `VALIDATION.REQUIRED` },
            { field: fieldName }
          )
        : `${fieldName} is required`
    );
  }

  return validator.notRequired().nullable();
};

export const poiValidationSchema = (intl?: IntlShape) =>
  Yup.object().shape({
    name: textFieldValidation(
      intl ? intl.formatMessage({ id: 'POI.FORM.NAME' }) : 'POI Name',
      2,
      100,
      true,
      intl
    ),
    category_id: Yup.string()
      .typeError(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.CATEGORY_REQUIRED' })
          : 'Category is required'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.CATEGORY_REQUIRED' })
          : 'Category is required'
      ),
    subtype_id: Yup.string()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .when('category_id', {
        is: (categoryId: string) => !!categoryId,
        then: (schema) =>
          schema.required(
            intl
              ? intl.formatMessage({ id: 'POI.VALIDATION.SUBTYPE_REQUIRED' })
              : 'Subtype is required'
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    latitude: Yup.number()
      .transform(transformLocalizedNumber)
      .typeError(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_NUMBER' })
          : 'Latitude must be a number'
      )
      .min(
        -90,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_RANGE' })
          : 'Latitude must be between -90 and 90'
      )
      .max(
        90,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_RANGE' })
          : 'Latitude must be between -90 and 90'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_REQUIRED' })
          : 'Latitude is required'
      ),
    longitude: Yup.number()
      .transform(transformLocalizedNumber)
      .typeError(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_NUMBER' })
          : 'Longitude must be a number'
      )
      .min(
        -180,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_RANGE' })
          : 'Longitude must be between -180 and 180'
      )
      .max(
        180,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_RANGE' })
          : 'Longitude must be between -180 and 180'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_REQUIRED' })
          : 'Longitude is required'
      ),
    description: Yup.string()
      .max(
        1000,
        intl
          ? intl.formatMessage(
              { id: 'POI.VALIDATION.DESCRIPTION_MAX' },
              { count: 1000 }
            )
          : 'Description cannot exceed 1000 characters'
      )
      .notRequired(),
    depth: Yup.string()
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.DEPTH_FORMAT' })
          : "Depth must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      )
      .notRequired(),
    depth_unit: Yup.string()
      .oneOf(
        ['m', 'ft'],
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.DEPTH_UNIT_INVALID' })
          : 'Depth unit must be either meters (m) or feet (ft)'
      )
      .notRequired(),
    tags: Yup.array()
      .of(
        Yup.string().max(
          50,
          intl
            ? intl.formatMessage(
                { id: 'POI.VALIDATION.TAG_MAX' },
                { count: 50 }
              )
            : 'Tag cannot exceed 50 characters'
        )
      )
      .max(
        10,
        intl
          ? intl.formatMessage({ id: 'POI.VALIDATION.TAGS_MAX' }, { count: 10 })
          : 'Maximum 10 tags allowed'
      )
      .notRequired(),
    images: Yup.array()
      .of(
        Yup.mixed().test(
          'is-file-or-url',
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.IMAGE_INVALID' })
            : 'Must be a valid file or URL',
          (value) => {
            if (typeof value === 'string') {
              try {
                new URL(value);
                return true;
              } catch {
                return false;
              }
            }
            return value instanceof File;
          }
        )
      )
      .max(
        5,
        intl
          ? intl.formatMessage(
              { id: 'POI.VALIDATION.IMAGES_MAX' },
              { count: 5 }
            )
          : 'Maximum 5 images allowed'
      )
      .notRequired(),
  });

export const yachtValidationSchema = (intl?: IntlShape) =>
  Yup.object().shape({
    name: textFieldValidation('Yacht Name', 2, 50, true, intl),
    location: textFieldValidation('Location', 5, 200, true, intl),
    description: Yup.string()
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Description' }
            )
          : 'Description is required'
      )
      .min(
        10,
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.MIN_LENGTH' },
              { field: 'Description', count: 10 }
            )
          : 'Description must be at least 10 characters'
      ),
    website: Yup.string()
      .url(
        intl
          ? intl.formatMessage({ id: 'VALIDATION.URL_INVALID' })
          : 'Must be a valid URL'
      )
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Website' }
            )
          : 'Website is required'
      ),
    contact_email: emailValidation(true, intl),
    logo: Yup.string().required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.REQUIRED' }, { field: 'Logo' })
        : 'Logo is required'
    ),
    yacht_club_admin_id: Yup.string().required(
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: 'Admin selection' }
          )
        : 'Admin selection is required'
    ),
  });

export const harborValidationSchema = (intl?: IntlShape) =>
  Yup.object().shape({
    name: Yup.string()
      .test(
        'no-leading-space',
        intl
          ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
          : 'Cannot start with spaces',
        (value) => {
          if (!value) return true;
          return !value.startsWith(' ');
        }
      )
      .min(
        2,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_MIN' })
          : 'Harbour Name must be at least 2 characters'
      )
      .max(
        50,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_MAX' })
          : 'Harbour Name must be at most 50 characters'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_REQUIRED' })
          : 'Harbour Name is required'
      ),
    status: Yup.string().required(
      intl
        ? intl.formatMessage({ id: 'VALIDATION.REQUIRED' }, { field: 'Status' })
        : 'Status is required'
    ),
    booking_type: Yup.string()
      .oneOf(Object.values(HarborBookingType))
      .notRequired(),
    category: Yup.string()
      .typeError(
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.CATEGORY_INVALID' })
          : 'Category is required'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.CATEGORY_INVALID' })
          : 'Category is required'
      ),
    location: Yup.string()
      .max(
        200,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LOCATION_MAX_LENGTH' })
          : 'Location must be at most 200 characters'
      )
      .when('category', {
        is: 'guest_harbour',
        then: (schema) =>
          schema.required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.LOCATION_REQUIRED',
                })
              : 'Location is required'
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    latitude: Yup.number()
      .transform(transformLocalizedNumber)
      .typeError(
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.LATITUDE_MUST_BE_NUMBER',
            })
          : 'Latitude must be a number'
      )
      .min(
        -90,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LATITUDE_MIN' })
          : 'Latitude must be at least -90'
      )
      .max(
        90,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LATITUDE_MAX' })
          : 'Latitude cannot exceed 90'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LATITUDE_REQUIRED' })
          : 'Latitude is required'
      ),
    longitude: Yup.number()
      .transform(transformLocalizedNumber)
      .typeError(
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.LONGITUDE_MUST_BE_NUMBER',
            })
          : 'Longitude must be a number'
      )
      .min(
        -180,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LONGITUDE_MIN' })
          : 'Longitude must be at least -180'
      )
      .max(
        180,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LONGITUDE_MAX' })
          : 'Longitude cannot exceed 180'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LONGITUDE_REQUIRED' })
          : 'Longitude is required'
      ),

    description: Yup.string()
      .max(
        2000,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.DESCRIPTION_MAX' })
          : 'Description cannot exceed 2000 characters'
      )
      .when('category', {
        is: 'guest_harbour',
        then: (schema) =>
          schema.required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.DESCRIPTION_REQUIRED',
                })
              : 'Description is required for guest harbors'
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    contact_email: Yup.string().when('category', {
      is: (cat: string) =>
        ['guest_harbour', 'municipal_guest_harbour'].includes(cat),
      then: (schema) =>
        schema
          .required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.CONTACT_EMAIL_REQUIRED',
                })
              : 'Contact email is required for this harbor category'
          )
          .email(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.INVALID_EMAIL_FORMAT',
                })
              : 'Must be a valid email format'
          ),
      otherwise: (schema) =>
        schema.notRequired().test(
          'valid-email-if-provided',
          intl
            ? intl.formatMessage({
                id: 'HARBOR.VALIDATION.INVALID_EMAIL_FORMAT',
              })
            : 'Must be a valid email format',
          (value) =>
            !value ||
            value.trim() === '' ||
            Yup.string().email().isValidSync(value)
        ),
    }),
    contact_phone: Yup.string().when('category', {
      is: (cat: string) =>
        ['guest_harbour', 'municipal_guest_harbour'].includes(cat),
      then: () =>
        Yup.string()
          .test(
            'is-valid-phone',
            intl
              ? intl.formatMessage(
                  { id: 'VALIDATION.INVALID_PHONE_NUMBER' },
                  { field: 'Contact Phone' }
                )
              : 'Contact Phone is not a valid mobile number',
            function (value) {
              if (!value) return true;
              const countryShortCode =
                this.parent?.contact_phone_country_short_code ||
                this.parent?.country_short_code;
              const callingCode = this.parent?.country_code;
              if (!countryShortCode && !callingCode) return true;
              return isValidCanonicalPhone(
                value,
                countryShortCode,
                callingCode
              );
            }
          )
          .required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.CONTACT_PHONE_REQUIRED',
                })
              : 'Contact Phone is required'
          ),
      otherwise: () =>
        Yup.string()
          .test(
            'is-valid-phone',
            intl
              ? intl.formatMessage(
                  { id: 'VALIDATION.INVALID_PHONE_NUMBER' },
                  { field: 'Contact Phone' }
                )
              : 'Contact Phone is not a valid mobile number',
            function (value) {
              if (!value) return true;
              const countryShortCode =
                this.parent?.contact_phone_country_short_code ||
                this.parent?.country_short_code;
              const callingCode = this.parent?.country_code;
              if (!countryShortCode && !callingCode) return true;
              return isValidCanonicalPhone(
                value,
                countryShortCode,
                callingCode
              );
            }
          )
          .nullable(),
    }),
    country_code: Yup.string()
      .matches(
        /^\+\d{1,3}$/,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.INVALID_COUNTRY_CODE' })
          : 'Invalid country code (e.g., +1, +91)'
      )
      .notRequired(),
    country_short_code: Yup.string().required(
      intl
        ? intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: 'Country' }
          )
        : 'Country is required'
    ),
    contact_phone_country_short_code: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .notRequired(),
    website: Yup.string()
      .url(
        intl
          ? intl.formatMessage({ id: 'VALIDATION.URL_INVALID' })
          : 'Must be a valid URL'
      )
      .max(
        200,
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.MAX_LENGTH' },
              { field: 'Website', count: 200 }
            )
          : 'Website must be at most 200 characters'
      )
      .notRequired()
      .nullable(),
    berth_capacity: numberFieldValidation(
      'Berth Capacity',
      1,
      10000,
      false
    ).notRequired(),
    min_depth: Yup.string()
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.MIN_DEPTH_FORMAT' })
          : "Minimum Depth must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      )
      .notRequired(),
    max_depth: Yup.string()
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.MAX_DEPTH_FORMAT',
            })
          : "Maximum Depth must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      )
      .notRequired(),
    depth_unit: Yup.string()
      .oneOf(
        ['m', 'ft'],
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.INVALID_DEPTH_UNIT',
            })
          : 'Invalid depth unit'
      )
      .notRequired(),
    default_check_in_time: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-in time must be in HH:mm format',
        excludeEmptyString: true,
      })
      .notRequired(),
    default_check_out_time: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-out time must be in HH:mm format',
        excludeEmptyString: true,
      })
      .notRequired(),
    check_in_window_start: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-in window start must be in HH:mm format',
        excludeEmptyString: true,
      })
      .notRequired(),
    check_in_window_end: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-in window end must be in HH:mm format',
        excludeEmptyString: true,
      })
      .test(
        'check-in-window-pair',
        'Both check-in window start and end are required',
        function (value) {
          const { check_in_window_start } = this.parent;
          return (
            (!check_in_window_start && !value) ||
            (!!check_in_window_start && !!value)
          );
        }
      )
      .test(
        'check-in-window-order',
        'Check-in window end must be after start',
        function (value) {
          const { check_in_window_start } = this.parent;
          if (!check_in_window_start || !value) return true;
          return value > check_in_window_start;
        }
      )
      .notRequired(),
    check_out_window_start: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-out window start must be in HH:mm format',
        excludeEmptyString: true,
      })
      .notRequired(),
    check_out_window_end: Yup.string()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, {
        message: 'Check-out window end must be in HH:mm format',
        excludeEmptyString: true,
      })
      .test(
        'check-out-window-pair',
        'Both check-out window start and end are required',
        function (value) {
          const { check_out_window_start } = this.parent;
          return (
            (!check_out_window_start && !value) ||
            (!!check_out_window_start && !!value)
          );
        }
      )
      .test(
        'check-out-window-order',
        'Check-out window end must be after start',
        function (value) {
          const { check_out_window_start } = this.parent;
          if (!check_out_window_start || !value) return true;
          return value > check_out_window_start;
        }
      )
      .notRequired(),
    timezone: Yup.string().notRequired(),
    facilities: Yup.array()
      .of(
        Yup.string().required(
          intl
            ? intl.formatMessage(
                { id: 'VALIDATION.REQUIRED' },
                { field: 'Facility' }
              )
            : 'Facility must be a string'
        )
      )
      .notRequired()
      .nullable(),
    businessHours: businessHoursValidation(),
    logo: imageValidation(false, 5),
    fairway_map: Yup.array()
      .of(imageValidation(false, 2, 10, 'fairway_map', intl))
      .max(
        5,
        intl
          ? intl.formatMessage({ id: 'VALIDATION.MAX_FILES' }, { count: 5 })
          : 'Maximum 5 files allowed'
      ),
    fairway_entrance_map: Yup.array()
      .of(imageValidation(false, 2, 10, 'fairway_entrance_map', intl))
      .max(
        5,
        intl
          ? intl.formatMessage({ id: 'VALIDATION.MAX_FILES' }, { count: 5 })
          : 'Maximum 5 files allowed'
      ),
    media: Yup.array()
      .of(mediaValidation(false, 5, 10, 2, 1))
      .max(
        5,
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.MEDIA_MAX_FILES',
            })
          : 'Maximum 5 media files allowed'
      )
      .nullable(),
    marina_id: Yup.string().notRequired(),
    vhf_channel: Yup.string()
      .transform((value) => {
        const trimmed = typeof value === 'string' ? value.trim() : value;
        return trimmed === '' || trimmed == null ? undefined : trimmed;
      })
      .matches(
        /^\d+$/,
        intl
          ? intl.formatMessage({
              id: 'HARBOR.VALIDATION.VHF_DIGITS_ONLY',
            })
          : 'VHF Channel must contain only digits'
      )
      .min(
        1,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.VHF_MIN_LENGTH' })
          : 'VHF Channel must be at least 1 digit'
      )
      .max(
        3,
        intl
          ? intl.formatMessage({ id: 'HARBOR.VALIDATION.VHF_MAX_LENGTH' })
          : 'VHF Channel must be at most 3 digits'
      )
      .notRequired(),
    regionIds: Yup.array()
      .of(
        Yup.string().required(
          intl
            ? intl.formatMessage(
                { id: 'VALIDATION.REQUIRED' },
                { field: 'Region ID' }
              )
            : 'Region ID must be a string'
        )
      )
      .notRequired()
      .nullable(),
    yachtClubIds: Yup.array()
      .of(
        Yup.string().required(
          intl
            ? intl.formatMessage(
                { id: 'VALIDATION.REQUIRED' },
                { field: 'Yacht Club ID' }
              )
            : 'Yacht Club ID must be a string'
        )
      )
      .notRequired()
      .nullable(),
    booking_enabled: Yup.boolean().notRequired(),
    // send_booking_to_admin: Yup.boolean().required(
    //   intl
    //     ? intl.formatMessage({
    //         id: "HARBOR.VALIDATION.BOOKING_APPROVAL_REQUIRED",
    //       })
    //     : "Please select an option for sending booking requests."
    // ),
    admin_notes: Yup.string().notRequired(),
    primary_language_spoken: Yup.string().notRequired(),
    coastal_area: Yup.string().required(
      intl
        ? intl.formatMessage({ id: 'HARBOR.VALIDATION.COASTAL_AREA_REQUIRED' })
        : 'Coastal Area is required'
    ),
    seasonality_support: Yup.boolean().notRequired(),
    // Ignore seasonal_dates for harbor schema to avoid interfering validations
    seasonal_dates: Yup.mixed().notRequired().nullable().strip(),
  });

export const berthValidationSchema = Yup.object().shape({
  category_id: Yup.string().required('Category is required'),
  berth_type: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === undefined) return undefined;
      return typeof originalValue === 'string' ? Number(originalValue) : value;
    })
    .typeError('Berth type is required')
    .oneOf(
      [BerthType.TRANSIENT, BerthType.SEASONAL, BerthType.YEARLY],
      'Invalid berth type'
    )
    .when('mode', {
      is: (value: string) => value === 'create' || value === 'edit',
      then: (schema) => schema.required('Berth type is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  seasonal_pricing: Yup.object()
    .nullable()
    .when('berth_type', {
      is: (berthType: unknown) => Number(berthType) === BerthType.SEASONAL,
      then: () =>
        Yup.object()
          .notRequired()
          .nullable()
          .shape({
            [Seasons.SPRING]: Yup.number()
              .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === undefined)
                  return undefined;
                return typeof originalValue === 'string'
                  ? Number(originalValue)
                  : value;
              })
              .typeError('Must be a number')
              .test(
                'spring-positive-when-provided',
                'Price must be positive',
                (value) =>
                  value === undefined ||
                  value === null ||
                  value === 0 ||
                  value > 0
              )
              .notRequired()
              .nullable(),
            [Seasons.SUMMER]: Yup.number()
              .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === undefined)
                  return undefined;
                return typeof originalValue === 'string'
                  ? Number(originalValue)
                  : value;
              })
              .typeError('Must be a number')
              .test(
                'summer-positive-when-provided',
                'Price must be positive',
                (value) =>
                  value === undefined ||
                  value === null ||
                  value === 0 ||
                  value > 0
              )
              .notRequired()
              .nullable(),
            [Seasons.AUTUMN]: Yup.number()
              .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === undefined)
                  return undefined;
                return typeof originalValue === 'string'
                  ? Number(originalValue)
                  : value;
              })
              .typeError('Must be a number')
              .test(
                'autumn-positive-when-provided',
                'Price must be positive',
                (value) =>
                  value === undefined ||
                  value === null ||
                  value === 0 ||
                  value > 0
              )
              .notRequired()
              .nullable(),
            [Seasons.WINTER]: Yup.number()
              .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === undefined)
                  return undefined;
                return typeof originalValue === 'string'
                  ? Number(originalValue)
                  : value;
              })
              .typeError('Must be a number')
              .test(
                'winter-positive-when-provided',
                'Price must be positive',
                (value) =>
                  value === undefined ||
                  value === null ||
                  value === 0 ||
                  value > 0
              )
              .notRequired()
              .nullable(),
          }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  price: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === undefined) return undefined;
      return typeof originalValue === 'string' ? Number(originalValue) : value;
    })
    .typeError('Must be a number')
    .min(0, 'Minimum price is 0')
    .max(100000, 'Maximum price is 100,000')
    .when('berth_type', {
      is: (berthType: unknown) => Number(berthType) === BerthType.TRANSIENT,
      then: (schema) =>
        schema.required('Price is required').positive('Price must be positive'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  quantity: Yup.number()
    .typeError('Must be a number')
    .positive('Quantity must be positive')
    .integer('Quantity must be a whole number')
    .min(1, 'Minimum quantity is 1')
    .max(100, 'Maximum quantity is 100')
    .when('mode', {
      is: (value: string) => value === 'create',
      then: (schema) => schema.required('Quantity is required in create mode'),
      otherwise: (schema) => schema.notRequired(),
    }),
  status: Yup.string().required('Status is required'),
  amenities: Yup.array().of(Yup.string()),
  pier_prefix: Yup.string().when('mode', {
    is: (value: string) => value === 'create',
    then: (schema) =>
      schema
        .required('Berth prefix is required in create mode')
        .min(1, 'Prefix must be at least 1 character')
        .matches(
          /^[\p{L}0-9-]+$/u,
          'Prefix can only contain letters, numbers, or hyphens'
        ),
    otherwise: (schema) =>
      schema
        .notRequired()
        .min(1, 'Prefix must be at least 1 character')
        .matches(
          /^[\p{L}0-9-]+$/u,
          'Prefix can only contain letters, numbers, or hyphens'
        ),
  }),
  pier_id: Yup.string().when('mode', {
    is: (value: string) => value === 'create',
    then: (schema) => schema.required('Pier is required in create mode'),
    otherwise: (schema) => schema.notRequired(),
  }),
  berth_number: Yup.string().when('mode', {
    is: (value: string) => value === 'edit' || value === 'view',
    then: (schema) =>
      schema
        .required('Berth number is required')
        .min(1, 'Berth number must be at least 1 character')
        .max(20, 'Berth number must not exceed 20 characters')
        .matches(
          /^[\p{L}0-9-]+$/u,
          'Berth number can only contain letters, numbers, or hyphens'
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  mooring_style: Yup.string().when('mode', {
    is: (value: string) => value === 'create',
    then: (schema) =>
      schema.required('Mooring style is required in create mode'),
    otherwise: (schema) => schema.required('Mooring style is required'),
  }),
  start_date: Yup.string().when('mode', {
    is: (value: string) => value === 'edit' || value === 'view',
    then: (schema) => schema.required('Start date is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  end_date: Yup.string().when('mode', {
    is: (value: string) => value === 'edit' || value === 'view',
    then: (schema) =>
      schema
        .required('End date is required')
        .test(
          'is-greater-or-equal',
          'End date must be on or after start date',
          function (end_date) {
            const { start_date } = this.parent;
            if (!start_date || !end_date) return true;
            return new Date(end_date) >= new Date(start_date);
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const berthCategoryValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('This field is required')
    .max(100, 'Maximum 100 characters allowed')
    .typeError('Must be a valid text'),
  description: textFieldValidation('Description', 0, 500, false),
  min_length: Yup.number()
    .typeError('Must be a number')
    .required('Minimum length is required')
    .positive('Length must be positive')
    .min(1, 'Minimum length is 1m')
    .max(1000, 'Maximum length is 1000m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  max_length: Yup.number()
    .typeError('Must be a number')
    .required('Maximum length is required')
    .positive('Length must be positive')
    .min(1, 'Minimum length is 1m')
    .max(1000, 'Maximum length is 1000m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    )
    .moreThan(
      Yup.ref('min_length'),
      'Maximum length must be greater than minimum length'
    ),
  min_width: Yup.number()
    .typeError('Must be a number')
    .required('Minimum width is required')
    .positive('Width must be positive')
    .min(1, 'Minimum width is 1m')
    .max(100, 'Maximum width is 100m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  max_width: Yup.number()
    .typeError('Must be a number')
    .required('Maximum width is required')
    .positive('Width must be positive')
    .min(1, 'Minimum width is 1m')
    .max(100, 'Maximum width is 100m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    )
    .moreThan(
      Yup.ref('min_width'),
      'Maximum width must be greater than minimum width'
    ),
  min_depth: Yup.number()
    .typeError('Must be a number')
    .required('Minimum depth is required')
    .positive('Depth must be positive')
    .min(0, 'Minimum depth is 1m')
    .max(100, 'Maximum depth is 100m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  max_depth: Yup.number()
    .typeError('Must be a number')
    .required('Maximum depth is required')
    .positive('Depth must be positive')
    .min(0, 'Minimum depth is 1m')
    .max(100, 'Maximum depth is 100m')
    .test(
      'decimal-places',
      'Maximum 2 decimal places allowed',
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    )
    .moreThan(
      Yup.ref('min_depth'),
      'Maximum depth must be greater than minimum depth'
    ),
  status: Yup.number().required('Status is required'),
});

export const addonValidationSchema = Yup.object().shape({
  harbor_id: Yup.string().required('Harbour is required'),
  name: textFieldValidation('Addon Name', 2, 50, true),
  description: textFieldValidation('Description', 0, 500, false),
  price: numberFieldValidation('Price', 0, 10000, true),
  quantity: numberFieldValidation('Quantity', 0, 1000, true),
  price_unit: Yup.string(),
  is_available: Yup.boolean(),
  status: Yup.string(),
  transaction_presentation_mode: Yup.string().nullable().notRequired(),
  access_handover_mode: Yup.string()
    .oneOf(['NONE', 'KEY_BOX', 'ACCESS_CODE'])
    .notRequired(),
  key_box_code: Yup.string().when('access_handover_mode', {
    is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
    then: (schema) => schema.required('Key box code is required').trim(),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  key_box_pickup_instructions: Yup.string().when('access_handover_mode', {
    is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
    then: (schema) =>
      schema.required('Pickup / usage instructions are required').trim(),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  key_box_return_instructions: Yup.string().when('access_handover_mode', {
    is: 'KEY_BOX',
    then: (schema) =>
      schema.required('Return instructions are required').trim(),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

export const promoValidationSchema = Yup.object().shape({
  name: textFieldValidation('Promo Name', 2, 100, true),
  code: textFieldValidation('Promo Code', 2, 50, true),
  description: textFieldValidation('Description', 0, 500, false),
  start_date: Yup.string().required('Start date is required'),
  end_date: Yup.string()
    .required('End date is required')
    .test(
      'is-after-start-date',
      'End date must be after start date',
      function (endDate) {
        const startDate = this.parent.start_date;
        if (!startDate || !endDate) return true;
        return new Date(endDate) > new Date(startDate);
      }
    ),
  coupon_type: Yup.number().required('Coupon type is required'),
  discount_type: Yup.number().required('Discount type is required'),
  discount_amount: Yup.number().when('discount_type', {
    is: DiscountType.FIXED_AMOUNT,
    then: (schema) =>
      schema
        .required('Discount amount is required')
        .positive('Must be greater than 0'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  // When Percentage discount is selected, percentage and max discount are mandatory
  discount_percentage: Yup.number().when('discount_type', {
    is: (value: string | number) => Number(value) === DiscountType.PERCENTAGE,
    then: (schema) =>
      schema
        .required('Discount percentage is required')
        .positive('Must be greater than 0')
        .max(100, 'Cannot exceed 100%'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  max_discount: Yup.number().when('discount_type', {
    is: (value: string | number) => Number(value) === DiscountType.PERCENTAGE,
    then: (schema) =>
      schema
        .required('Max discount amount is required for percentage discounts')
        .positive('Must be greater than 0'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  min_order_value: numberFieldValidation('Min Order Value', 0, 100000, true),
  total_quantity: numberFieldValidation('Total Quantity', 1, 1000000, false),
  remaining_usage_count: numberFieldValidation(
    'Remaining Usage Count',
    0,
    1000000,
    false
  ),
  customer_usage_limit: numberFieldValidation(
    'Customer Usage Limit',
    1,
    1000,
    false
  ),
  frequency: Yup.number().notRequired().nullable(),
  status: Yup.number().required('Status is required'),
  is_restricted_to_groups: Yup.boolean().notRequired(),
  is_supplier_promotion: Yup.boolean().notRequired(),
  linked_club_id: Yup.string().when('eligibility_rule', {
    is: (value: number | undefined) =>
      value === CouponEligibilityRule.CLUB_MEMBER_ONLY,
    then: (schema) => schema.required('Linked club is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

export const berthGroupValidationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be at most 50 characters')
    .required('Name is required'),
  harbor_id: Yup.string().required('Harbour is required'),
});

export const communityValidationSchema = Yup.object().shape({
  moderator_ids: Yup.array().required('Moderator is required'),
  name: textFieldValidation('Community Name', 2, 50, true),
  description: textFieldValidation('Description', 0, 500, false),
  status: Yup.string()
    .oneOf(
      ['OPEN', 'PRIVATE', 'GLOBAL', 'CLOSED', 'DELETED'],
      'Status must be either OPEN, PRIVATE, GLOBAL, CLOSED, or DELETED'
    )
    .required('Status is required'),
});

export const pierValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Pier Name is required')
    .min(2, 'Pier Name must be at least 2 characters')
    .max(50, 'Pier Name must be at most 50 characters'),
  harbor_id: Yup.string().required('Harbour is required'),
  boater_label: Yup.string()
    .max(150, 'Pier description must be at most 150 characters')
    .nullable()
    .optional(),
  approach_note: Yup.string()
    .max(300, 'Approach instructions must be at most 300 characters')
    .nullable()
    .optional(),
  boater_label_translations: Yup.object()
    .shape({
      fi: Yup.string()
        .max(500, 'Finnish pier description must be at most 500 characters')
        .optional(),
      sv: Yup.string()
        .max(500, 'Swedish pier description must be at most 500 characters')
        .optional(),
      en: Yup.string()
        .max(500, 'English pier description must be at most 500 characters')
        .optional(),
      de: Yup.string()
        .max(500, 'German pier description must be at most 500 characters')
        .optional(),
    })
    .nullable()
    .optional(),
  approach_note_translations: Yup.object()
    .shape({
      fi: Yup.string()
        .max(
          500,
          'Finnish approach instructions must be at most 500 characters'
        )
        .optional(),
      sv: Yup.string()
        .max(
          500,
          'Swedish approach instructions must be at most 500 characters'
        )
        .optional(),
      en: Yup.string()
        .max(
          500,
          'English approach instructions must be at most 500 characters'
        )
        .optional(),
      de: Yup.string()
        .max(500, 'German approach instructions must be at most 500 characters')
        .optional(),
    })
    .nullable()
    .optional(),
  capacity_mode: Yup.string()
    .oneOf(
      ['ENUMERATED', 'POOLED', 'SINGLE_BERTH_POOL'],
      'Capacity mode must be enumerated, pooled, or single-berth pool'
    )
    .optional(),
  pooled_capacity: Yup.number()
    .transform((value) => (isNaN(value as number) ? undefined : value))
    .typeError('Pooled capacity must be a number')
    .integer('Pooled capacity must be an integer')
    .min(1, 'Pooled capacity must be at least 1')
    .when('capacity_mode', {
      is: (val: unknown) => val === 'POOLED' || val === 'SINGLE_BERTH_POOL',
      then: (schema) => schema.required('Pooled capacity is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const createOrganizationValidationSchema = (
  mode: 'view' | 'edit' | 'create',
  intl?: IntlShape
) => {
  return Yup.object().shape({
    organisation_name: Yup.string()
      .required(
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.NAME_REQUIRED' })
          : 'Organisation Name is required'
      )
      .min(
        2,
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.NAME_MIN' })
          : 'Organisation Name must be at least 2 characters'
      )
      .max(
        100,
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.NAME_MAX' })
          : 'Organisation Name must be at most 100 characters'
      ),
    organisation_type: Yup.string()
      .oneOf(['DMO', 'Regional Tourism Board', 'City Marketing'])
      .required(
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.TYPE_REQUIRED' })
          : 'Organisation Type is required'
      ),
    contact_email: Yup.string()
      .email(
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.EMAIL_INVALID' })
          : 'Invalid email format'
      )
      .required(
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.EMAIL_REQUIRED' })
          : 'Contact Email is required'
      ),
    public_homepage: Yup.string()
      .url(
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.URL_INVALID' })
          : 'Invalid URL'
      )
      .notRequired(),
    description: textFieldValidation(
      'Description',
      0,
      500,
      false,
      intl
    ).nullable(),
    user_id:
      mode === 'create'
        ? Yup.string().required(
            intl
              ? intl.formatMessage({
                  id: 'ORGANIZATION.VALIDATION.ADMIN_REQUIRED',
                })
              : 'Admin is required'
          )
        : Yup.string().notRequired(),
    linked_regions: Yup.array()
      .of(Yup.string())
      .min(
        1,
        intl
          ? intl.formatMessage({ id: 'ORGANIZATION.VALIDATION.REGIONS_MIN' })
          : 'Please select at least one region'
      )
      .required(
        intl
          ? intl.formatMessage({
              id: 'ORGANIZATION.VALIDATION.REGIONS_REQUIRED',
            })
          : 'Linked regions are required'
      ),
  });
};

type ServiceProviderFormData = {
  country_short_code?: string;
  vat_id?: string;
  contact_phone?: string;
  post_code?: string;
};

export const createServiceProviderValidationSchema = (
  mode: 'view' | 'edit' | 'create',
  intl?: IntlShape
) => {
  return Yup.object<ServiceProviderFormData>().shape({
    legal_name: textFieldValidation('Legal Name', 2, 255, true, intl),
    display_name: textFieldValidation('Display Name', 2, 255, true, intl),

    vat_id: Yup.string()
      .test('vat-format', function (value) {
        if (!value) return true;
        if (!this.parent?.country_short_code) return true;

        const countryCode = this.parent.country_short_code;

        const schema = vatIdValidation(countryCode, intl);

        try {
          schema.validateSync(value);
          return true;
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            return this.createError({
              message: err.message,
            });
          }
          return false;
        }
      })
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'VAT ID' }
            )
          : 'VAT ID is required'
      ),

    contact_email: Yup.string()
      .email(
        intl
          ? intl.formatMessage({ id: 'VALIDATION.EMAIL_INVALID' })
          : 'Invalid email format'
      )
      .max(100, 'Email must be at most 100 characters')
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Contact Email' }
            )
          : 'Contact Email is required'
      ),

    contact_phone: Yup.string()
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Contact Phone' }
            )
          : 'Contact Phone is required'
      )
      .test(
        'is-valid-phone',
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.INVALID_PHONE_NUMBER' },
              { field: 'Contact Phone' }
            )
          : 'Contact Phone is not a valid mobile number',
        function (value) {
          if (!value) return true;
          const countryShortCode =
            this.parent?.contact_phone_country_short_code ||
            this.parent?.country_short_code;
          const callingCode = this.parent?.country_code;
          if (!countryShortCode && !callingCode) return true;
          return isValidCanonicalPhone(value, countryShortCode, callingCode);
        }
      ),

    country_code: Yup.string()
      .max(10, 'Country code must be at most 10 characters')
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Country Code' }
            )
          : 'Country Code is required'
      ),

    country_short_code: Yup.string()
      .max(10, 'Country short code must be at most 10 characters')
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Country Short Code' }
            )
          : 'Country Short Code is required'
      ),

    contact_phone_country_short_code: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .notRequired(),

    website: Yup.string()
      .url(
        intl
          ? intl.formatMessage({ id: 'VALIDATION.URL_INVALID' })
          : 'Invalid website URL'
      )
      .max(255, 'Website must be at most 255 characters')
      .nullable()
      .notRequired(),

    description: Yup.string()
      .max(1000, 'Description must be at most 1000 characters')
      .nullable(),

    linked_harbor_ids: Yup.array()
      .of(Yup.string().uuid('Invalid harbor ID'))
      .min(1, 'At least one harbor must be selected')
      .required('At least one harbor must be selected'),

    street: Yup.string()
      .min(2, 'Street must be at least 2 characters')
      .max(255, 'Street must be at most 255 characters')
      .required('Street is required'),

    city: Yup.string()
      .min(2, 'City must be at least 2 characters')
      .max(100, 'City must be at most 100 characters')
      .required('City is required'),

    post_code: postcodeValidation(true, 'country_short_code'),

    country: Yup.string()
      .max(100, 'Country must be at most 100 characters')
      .required(
        intl
          ? intl.formatMessage(
              { id: 'VALIDATION.REQUIRED' },
              { field: 'Country' }
            )
          : 'Country is required'
      ),

    latitude: Yup.string()
      .test(
        'is-valid-latitude',
        'Latitude must be a valid number between -90 and 90',
        (value) => {
          if (!value) return true; // Optional field
          const num = parseFloat(value.replace(',', '.'));
          return !isNaN(num) && num >= -90 && num <= 90;
        }
      )
      .test(
        'is-valid-format',
        'Invalid latitude format (e.g., 45.6789)',
        (value) => {
          if (!value) return true; // Optional field
          return /^-?\d{1,2}([.,]\d+)?$/.test(value);
        }
      )
      .nullable()
      .notRequired(),

    longitude: Yup.string()
      .test(
        'is-valid-longitude',
        'Longitude must be a valid number between -180 and 180',
        (value) => {
          if (!value) return true; // Optional field
          const num = parseFloat(value.replace(',', '.'));
          return !isNaN(num) && num >= -180 && num <= 180;
        }
      )
      .test(
        'is-valid-format',
        'Invalid longitude format (e.g., -73.9876)',
        (value) => {
          if (!value) return true; // Optional field
          return /^-?\d{1,3}([.,]\d+)?$/.test(value);
        }
      )
      .nullable()
      .notRequired(),
  });
};

// Club Resource validation schema (i18n-aware)
export const getClubResourceValidationSchema = (intl?: IntlShape) => {
  const t = (id: string, defaultMessage: string) =>
    intl ? intl.formatMessage({ id }) : defaultMessage;

  return Yup.object().shape({
    name: Yup.string()
      .required(
        t('CLUB_RESOURCE.VALIDATION.NAME_REQUIRED', 'Resource name is required')
      )
      .min(
        2,
        t(
          'CLUB_RESOURCE.VALIDATION.NAME_MIN',
          'Resource name must be at least 2 characters'
        )
      )
      .max(
        100,
        t(
          'CLUB_RESOURCE.VALIDATION.NAME_MAX',
          'Resource name cannot exceed 100 characters'
        )
      )
      .typeError('Must be a valid text'),
    description: Yup.string()
      .max(
        500,
        t(
          'CLUB_RESOURCE.VALIDATION.DESCRIPTION_MAX',
          'Description cannot exceed 500 characters'
        )
      )
      .notRequired(),
    resource_type: Yup.number()
      .nullable()
      .oneOf(
        [1, 2, 3, 4, null],
        t(
          'CLUB_RESOURCE.VALIDATION.RESOURCE_TYPE_INVALID',
          'Invalid resource type'
        )
      )
      .notRequired(),
    allow_booking: Yup.boolean().notRequired(),
    quantity: Yup.number()
      .typeError(
        t('CLUB_RESOURCE.VALIDATION.QUANTITY_NUMBER', 'Must be a number')
      )
      .positive(
        t(
          'CLUB_RESOURCE.VALIDATION.QUANTITY_POSITIVE',
          'Quantity must be positive'
        )
      )
      .integer(
        t(
          'CLUB_RESOURCE.VALIDATION.QUANTITY_INTEGER',
          'Quantity must be a whole number'
        )
      )
      .min(
        1,
        t('CLUB_RESOURCE.VALIDATION.QUANTITY_MIN', 'Minimum quantity is 1')
      )
      .max(
        1000,
        t('CLUB_RESOURCE.VALIDATION.QUANTITY_MAX', 'Maximum quantity is 1000')
      )
      .notRequired(),
    slot_length: Yup.string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .test(
        'slot-format',
        t(
          'CLUB_RESOURCE.VALIDATION.SLOT_LENGTH_FORMAT',
          "Slot length must be in format like '2h', '3h', etc."
        ),
        function (value) {
          if (!value) return true; // Allow null/undefined/empty
          return /^\d+(h|m)$/.test(value);
        }
      )
      .when('resource_type', {
        is: (type: number) =>
          type !== undefined && type !== null && type !== 2 && type !== 4, // Not Keys or Port Watch Shifts
        then: (schema) =>
          schema.required(
            t(
              'CLUB_RESOURCE.VALIDATION.SLOT_LENGTH_REQUIRED',
              'Slot length is required for this resource type'
            )
          ),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    max_bookings_period_days: Yup.number()
      .typeError(
        t('CLUB_RESOURCE.VALIDATION.MAX_BOOKINGS_INTEGER', 'Must be a number')
      )
      .min(
        1,
        t('CLUB_RESOURCE.VALIDATION.MAX_BOOKINGS_MIN', 'Must be at least 1 day')
      )
      .max(
        365,
        t('CLUB_RESOURCE.VALIDATION.MAX_BOOKINGS_MAX', 'Cannot exceed 365 days')
      )
      .integer(
        t(
          'CLUB_RESOURCE.VALIDATION.MAX_BOOKINGS_INTEGER',
          'Must be a whole number'
        )
      )
      .when('resource_type', {
        is: 4, // PORT_WATCH_SHIFTS - not required
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required(
            t(
              'CLUB_RESOURCE.VALIDATION.MAX_BOOKINGS_REQUIRED',
              'Max booking period is required'
            )
          ),
      }),
    start_date: Yup.string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        t('CLUB_RESOURCE.VALIDATION.DATE_INVALID_FORMAT', 'Invalid date format')
      )
      .when('resource_type', {
        is: 4, // PORT_WATCH_SHIFTS - use shift_start_date instead
        then: (schema) => schema.notRequired().nullable().strip(),
        otherwise: (schema) =>
          schema
            .notRequired()
            .nullable()
            .test(
              'is-future-date',
              t(
                'CLUB_RESOURCE.VALIDATION.START_DATE_FUTURE',
                'Start date must be today or in the future'
              ),
              function (value) {
                if (!value) return true;
                const today = new Date().toISOString().split('T')[0];
                return value >= today;
              }
            ),
      }),
    end_date: Yup.string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        t('CLUB_RESOURCE.VALIDATION.DATE_INVALID_FORMAT', 'Invalid date format')
      )
      .when('resource_type', {
        is: 4, // PORT_WATCH_SHIFTS - use shift_end_date instead
        then: (schema) => schema.notRequired().nullable().strip(),
        otherwise: (schema) =>
          schema
            .notRequired()
            .nullable()
            .test(
              'is-after-start',
              t(
                'CLUB_RESOURCE.VALIDATION.END_DATE_AFTER_START',
                'End date must be after start date'
              ),
              function (value) {
                const { start_date } = this.parent;
                if (!value || !start_date) return true;
                return value > start_date;
              }
            ),
      }),
    time_shift: Yup.string()
      .oneOf(
        ['morning', 'afternoon', 'night', 'full_day'],
        'Invalid time shift'
      )
      .notRequired(),
    recurrence_pattern: Yup.string()
      .oneOf(
        ['single', 'daily', 'weekly', 'seasonal'],
        t(
          'CLUB_RESOURCE.VALIDATION.RECURRENCE_INVALID',
          'Invalid recurrence pattern'
        )
      )
      .notRequired()
      .nullable(),
    define_price: Yup.boolean().notRequired(),
    price: Yup.number()
      .typeError(
        t('CLUB_RESOURCE.VALIDATION.PRICE_REQUIRED', 'Must be a number')
      )
      .min(
        0,
        t(
          'CLUB_RESOURCE.VALIDATION.PRICE_NON_NEGATIVE',
          'Price cannot be negative'
        )
      )
      .when('define_price', {
        is: true,
        then: (schema) =>
          schema.required(
            t(
              'CLUB_RESOURCE.VALIDATION.PRICE_REQUIRED',
              'Price is required when price is defined'
            )
          ),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    price_unit: Yup.string()
      .oneOf(
        ['PER_DAY', 'PER_HOUR', 'PER_SLOT'],
        t('CLUB_RESOURCE.VALIDATION.PRICE_UNIT_INVALID', 'Invalid price unit')
      )
      .when('define_price', {
        is: true,
        then: (schema) =>
          schema.required(
            t(
              'CLUB_RESOURCE.VALIDATION.PRICE_UNIT_REQUIRED',
              'Price unit is required when price is defined'
            )
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    seasonal_dates: Yup.object()
      .transform((value) => {
        if (!value) return value;

        // Helper to parse stringified values and handle nested stringified objects
        type JsonPrimitive = string | number | boolean | null;
        type JsonValue = JsonPrimitive | JsonObject | JsonArray;
        interface JsonObject {
          [key: string]: JsonValue;
        }
        type JsonArray = JsonValue[];

        const deepParse = (val: JsonValue): JsonValue => {
          if (typeof val === 'string') {
            try {
              // Try to parse the string as JSON
              const parsed = JSON.parse(val);
              // If parsing succeeds, recursively parse the result
              return deepParse(parsed);
            } catch {
              // If parsing fails, check if it's a stringified object without quotes
              if (val.startsWith('{') && val.endsWith('}')) {
                try {
                  // Try to parse as JSON with proper quotes
                  const fixed = val
                    .replace(/([[{\],]\s*)([a-zA-Z0-9_]+?):/g, '$1"$2":')
                    .replace(/:\s*'([^']*?)'/g, ':"$1"')
                    .replace(/:\s*([^\s{[},'"]+)([,}])/g, ':"$1"$2');
                  return deepParse(JSON.parse(fixed));
                } catch {
                  // If all parsing fails, return the original value
                  return val;
                }
              }
              return val;
            }
          } else if (Array.isArray(val)) {
            // Process each item in the array
            return val.map((item) => {
              if (typeof item === 'object' && item !== null) {
                // Process object properties
                const result: Record<string, JsonValue> = {};
                Object.entries(item).forEach(([key, value]) => {
                  result[key] = deepParse(value as JsonValue);
                });
                return result;
              } else if (typeof item === 'string') {
                // Try to parse string items
                return deepParse(item);
              }
              return item;
            });
          } else if (val && typeof val === 'object') {
            // Process object properties
            const result: Record<string, JsonValue> = {};
            Object.entries(val).forEach(([key, value]) => {
              result[key] = deepParse(value as JsonValue);
            });
            return result;
          }
          return val;
        };

        // Process the entire value
        const parsedValue = deepParse(value as JsonValue);

        // Ensure we have the correct structure
        if (Array.isArray(parsedValue)) {
          // Handle array format (legacy)
          const arr = parsedValue as unknown[];
          const pick = (idx: number) => {
            const v = arr[idx];
            return Array.isArray(v) ? (v as unknown[]) : [v].filter(Boolean);
          };
          return {
            spring: pick(0),
            summer: pick(1),
            autumn: pick(2),
            winter: pick(3),
          };
        }

        // Handle object format when parsedValue is a non-null object
        if (parsedValue !== null && typeof parsedValue === 'object') {
          const obj = parsedValue as Record<string, unknown>;
          const pickKey = (key: string) => {
            const v = obj[key];
            return Array.isArray(v) ? (v as unknown[]) : [v].filter(Boolean);
          };
          return {
            spring: pickKey('spring'),
            summer: pickKey('summer'),
            autumn: pickKey('autumn'),
            winter: pickKey('winter'),
          };
        }

        // Fallback: return original value when structure is not as expected
        return value;
      })
      .when('recurrence_pattern', {
        is: 'seasonal',
        then: (schema) =>
          schema
            .shape({
              spring: Yup.array()
                .of(
                  Yup.object().shape({
                    id: Yup.string(),
                    start_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('Start date is required')
                      .test(
                        'is-valid-start-date',
                        'Start date must be a valid date',
                        (value) => !isNaN(Date.parse(value || ''))
                      ),
                    end_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('End date is required')
                      .test(
                        'is-valid-end-date',
                        'End date must be after start date',
                        function (endDate) {
                          const { start_date } = this.parent;
                          if (!start_date || !endDate) return true;
                          return new Date(endDate) > new Date(start_date);
                        }
                      ),
                    is_closed: Yup.boolean().default(false),
                  })
                )
                .default(() => []),
              summer: Yup.array()
                .of(
                  Yup.object().shape({
                    id: Yup.string(),
                    start_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('Start date is required')
                      .test(
                        'is-valid-start-date',
                        'Start date must be a valid date',
                        (value) => !isNaN(Date.parse(value || ''))
                      ),
                    end_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('End date is required')
                      .test(
                        'is-valid-end-date',
                        'End date must be after start date',
                        function (endDate) {
                          const { start_date } = this.parent;
                          if (!start_date || !endDate) return true;
                          return new Date(endDate) > new Date(start_date);
                        }
                      ),
                    is_closed: Yup.boolean().default(false),
                  })
                )
                .default(() => []),
              autumn: Yup.array()
                .of(
                  Yup.object().shape({
                    id: Yup.string(),
                    start_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('Start date is required')
                      .test(
                        'is-valid-start-date',
                        'Start date must be a valid date',
                        (value) => !isNaN(Date.parse(value || ''))
                      ),
                    end_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('End date is required')
                      .test(
                        'is-valid-end-date',
                        'End date must be after start date',
                        function (endDate) {
                          const { start_date } = this.parent;
                          if (!start_date || !endDate) return true;
                          return new Date(endDate) > new Date(start_date);
                        }
                      ),
                    is_closed: Yup.boolean().default(false),
                  })
                )
                .default(() => []),
              winter: Yup.array()
                .of(
                  Yup.object().shape({
                    id: Yup.string(),
                    start_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('Start date is required')
                      .test(
                        'is-valid-start-date',
                        'Start date must be a valid date',
                        (value) => !isNaN(Date.parse(value || ''))
                      ),
                    end_date: Yup.string()
                      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                      .required('End date is required')
                      .test(
                        'is-valid-end-date',
                        'End date must be after start date',
                        function (endDate) {
                          const { start_date } = this.parent;
                          if (!start_date || !endDate) return true;
                          return new Date(endDate) > new Date(start_date);
                        }
                      ),
                    is_closed: Yup.boolean().default(false),
                  })
                )
                .default(() => []),
            })
            .test(
              'has-seasonal-dates',
              'At least one season must be defined',
              function (value) {
                if (!value) return false;
                const {
                  spring = [],
                  summer = [],
                  autumn = [],
                  winter = [],
                } = value;
                return (
                  spring.length > 0 ||
                  summer.length > 0 ||
                  autumn.length > 0 ||
                  winter.length > 0
                );
              }
            ),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    // Port Watch Shifts specific fields - required validation
    shift_start_date: Yup.string()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value
      )
      .when('resource_type', {
        is: 4, // PORT_WATCH_SHIFTS
        then: (schema) =>
          schema.when('recurrence_pattern', {
            is: 'seasonal',
            then: (ss) => ss.notRequired().nullable().strip(),
            otherwise: (s) =>
              s.when('recurrence_pattern', {
                is: 'single',
                then: (s1) =>
                  s1
                    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                    .required('Shift date is required')
                    .test(
                      'is-future-date',
                      'Shift start date must be today or in the future',
                      function (value) {
                        if (!value) return true;
                        // Skip future date validation in edit mode
                        if (this.options.context?.isEditMode) return true;
                        const today = new Date().toISOString().split('T')[0];
                        return value >= today;
                      }
                    ),
                otherwise: (so) =>
                  so
                    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                    .required('Shift start date is required')
                    .test(
                      'is-future-date',
                      'Shift start date must be today or in the future',
                      function (value) {
                        if (!value) return true;
                        // Skip future date validation in edit mode
                        if (this.options.context?.isEditMode) return true;
                        const today = new Date().toISOString().split('T')[0];
                        return value >= today;
                      }
                    ),
              }),
          }),
        otherwise: (schema) => schema.notRequired().nullable().strip(),
      }),
    shift_end_date: Yup.string()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value
      )
      .when('resource_type', {
        is: 4, // PORT_WATCH_SHIFTS
        then: (schema) =>
          schema.when('recurrence_pattern', {
            is: 'daily',
            then: (s) =>
              s
                .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                .notRequired()
                .nullable()
                .test(
                  'is-after-shift-start',
                  'Shift end date must be after shift start date',
                  function (value) {
                    const { shift_start_date } = this.parent;
                    if (!value || !shift_start_date) return true;
                    return value >= shift_start_date;
                  }
                ),
            otherwise: (s) => s.notRequired().nullable().strip(),
          }),
        otherwise: (schema) => schema.notRequired().nullable().strip(),
      }),
    shift_start_time: Yup.string().when('resource_type', {
      is: 4, // PORT_WATCH_SHIFTS
      then: (schema) =>
        schema
          .matches(
            /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
            t(
              'CLUB_RESOURCE.VALIDATION.DATE_INVALID_FORMAT',
              'Invalid time format (HH:MM)'
            )
          )
          .required(
            t(
              'CLUB_RESOURCE.VALIDATION.SHIFT_START_TIME_REQUIRED',
              'Shift start time is required'
            )
          ),
      otherwise: (schema) => schema.notRequired().nullable().strip(),
    }),
    // Port Watch Shifts - member liability (optional numeric)
    port_watch_duty_liability: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value
      )
      .typeError('Must be a number')
      .integer('Must be a whole number')
      .min(0, 'Cannot be negative')
      .when('resource_type', {
        is: 4, // Only applicable to PORT_WATCH_SHIFTS
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired().nullable().strip(),
      }),
    port_watch_buyout_amount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value
      )
      .typeError('Must be a number')
      .min(0, 'Cannot be negative')
      .when('resource_type', {
        is: 4,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired().nullable().strip(),
      }),
    shift_end_time: Yup.string().when('resource_type', {
      is: 4, // PORT_WATCH_SHIFTS
      then: (schema) =>
        schema
          .matches(
            /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
            t(
              'CLUB_RESOURCE.VALIDATION.DATE_INVALID_FORMAT',
              'Invalid time format (HH:MM)'
            )
          )
          .required(
            t(
              'CLUB_RESOURCE.VALIDATION.SHIFT_END_TIME_REQUIRED',
              'Shift end time is required'
            )
          )
          .test(
            'is-after-shift-start-time',
            t(
              'CLUB_RESOURCE.VALIDATION.SHIFT_END_TIME_AFTER_START',
              'Shift end time must be after shift start time'
            ),
            function (value) {
              const { shift_start_time, shift_start_date, shift_end_date } =
                this.parent;
              if (!value || !shift_start_time) return true;

              // If both dates are provided and equal, enforce end > start
              if (
                shift_start_date &&
                shift_end_date &&
                shift_start_date === shift_end_date
              ) {
                const [sh, sm] = shift_start_time.split(':').map(Number);
                const [eh, em] = value.split(':').map(Number);
                const startMinutes = sh * 60 + sm;
                const endMinutes = eh * 60 + em;
                return endMinutes > startMinutes;
              }

              // If single-day with only start date provided, allow overnight (end can be before start)
              // If dates differ (daily range), any time combination is valid
              return true;
            }
          ),
      otherwise: (schema) => schema.notRequired().nullable().strip(),
    }),
    status: Yup.number()
      .required('Status is required')
      .oneOf([0, 1], 'Status must be 0 or 1')
      .typeError('Must be a valid status'),
    // Sauna-specific fields
    weekly_windows: Yup.object().when('resource_type', {
      is: 1, // SAUNA_BOOKING
      then: (schema) =>
        schema
          // Always validate individual time windows when provided
          .test(
            'valid-time-windows',
            'All time windows must have valid start and end times',
            function (
              value:
                | Record<
                    string,
                    Array<{ start_time: string; end_time: string }>
                  >
                | null
                | undefined
            ) {
              if (!value) return true;
              const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
              const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
              for (const day of days) {
                if (value[day] && Array.isArray(value[day])) {
                  for (const window of value[day]) {
                    if (!window.start_time || !window.end_time) return false;
                    if (
                      !timeRegex.test(window.start_time) ||
                      !timeRegex.test(window.end_time)
                    )
                      return false;
                    const [sh, sm] = window.start_time.split(':').map(Number);
                    const [eh, em] = window.end_time.split(':').map(Number);
                    if (eh * 60 + em <= sh * 60 + sm) return false;
                  }
                }
              }
              return true;
            }
          )
          // Recurrence-based coverage rules
          .when('recurrence_pattern', {
            is: 'seasonal',
            then: (s) => s.notRequired().nullable(),
            otherwise: (s) =>
              s.when('recurrence_pattern', {
                is: 'daily',
                then: (sd) =>
                  sd
                    .test(
                      'daily-all-days-have-slots',
                      'For Daily recurrence, add at least one time window for every day (Mon-Sun)',
                      function (
                        value:
                          | Partial<
                              Record<
                                | 'mon'
                                | 'tue'
                                | 'wed'
                                | 'thu'
                                | 'fri'
                                | 'sat'
                                | 'sun',
                                Array<{ start_time: string; end_time: string }>
                              >
                            >
                          | null
                          | undefined
                      ) {
                        if (!value) return false;
                        const days: Array<
                          'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
                        > = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                        return days.every(
                          (day) =>
                            Array.isArray(value[day]) && value[day]!.length > 0
                        );
                      }
                    )
                    .required(
                      'Weekly time windows are required for Daily recurrence'
                    ),
                otherwise: (sd) =>
                  sd.when('recurrence_pattern', {
                    is: 'weekly',
                    then: (sw) =>
                      sw
                        .test(
                          'weekly-at-least-one-day',
                          'For Weekly recurrence, add at least one time window on any one day',
                          function (
                            value:
                              | Partial<
                                  Record<
                                    | 'mon'
                                    | 'tue'
                                    | 'wed'
                                    | 'thu'
                                    | 'fri'
                                    | 'sat'
                                    | 'sun',
                                    Array<{
                                      start_time: string;
                                      end_time: string;
                                    }>
                                  >
                                >
                              | null
                              | undefined
                          ) {
                            if (!value) return false;
                            const days: Array<
                              | 'mon'
                              | 'tue'
                              | 'wed'
                              | 'thu'
                              | 'fri'
                              | 'sat'
                              | 'sun'
                            > = [
                              'mon',
                              'tue',
                              'wed',
                              'thu',
                              'fri',
                              'sat',
                              'sun',
                            ];
                            const count = days.reduce((acc, day) => {
                              const arr = value[day];
                              return (
                                acc +
                                (Array.isArray(arr) && arr.length > 0 ? 1 : 0)
                              );
                            }, 0);
                            return count >= 1;
                          }
                        )
                        .required(
                          'Weekly time windows are required for Weekly recurrence'
                        ),
                    otherwise: (ss) => ss.notRequired().nullable(), // SINGLE or undefined
                  }),
              }),
          }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    special_date_windows: Yup.array().when('resource_type', {
      is: 1, // SAUNA_BOOKING
      then: (schema) =>
        schema
          .of(
            Yup.object().shape({
              date: Yup.string()
                .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
                .required('Date is required'),
              windows: Yup.array()
                .of(
                  Yup.object().shape({
                    start_time: Yup.string()
                      .matches(
                        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
                        'Invalid time format'
                      )
                      .required('Start time is required'),
                    end_time: Yup.string()
                      .matches(
                        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
                        'Invalid time format'
                      )
                      .required('End time is required')
                      .test(
                        'is-after-start',
                        'End time must be after start time',
                        function (value) {
                          const { start_time } = this.parent;
                          if (!value || !start_time) return true;
                          const [sh, sm] = start_time.split(':').map(Number);
                          const [eh, em] = value.split(':').map(Number);
                          const startMinutes = sh * 60 + sm;
                          const endMinutes = eh * 60 + em;
                          return endMinutes > startMinutes;
                        }
                      ),
                  })
                )
                .min(1, 'At least one time window is required')
                .required('Time windows are required'),
            })
          )
          .notRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    closed_dates: Yup.array().when('resource_type', {
      is: 1, // SAUNA_BOOKING
      then: (schema) =>
        schema
          .of(
            Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
          )
          .notRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });
};

export const clubResourceValidationSchema = getClubResourceValidationSchema();

// Service Management validation schema
export const serviceValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Service name is required')
    .min(2, 'Service name must be at least 2 characters')
    .max(100, 'Service name cannot exceed 100 characters')
    .typeError('Must be a valid text'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(1000, 'Description cannot exceed 1000 characters')
    .typeError('Must be a valid text'),
  category: Yup.string()
    .required('Category is required')
    .min(2, 'Category must be at least 2 characters')
    .max(50, 'Category cannot exceed 50 characters')
    .typeError('Must be a valid text'),
  sub_category: Yup.string()
    .required('Sub category is required')
    .min(2, 'Sub category must be at least 2 characters')
    .max(50, 'Sub category cannot exceed 50 characters')
    .typeError('Must be a valid text'),
  service_type: Yup.string()
    .required('Service type is required')
    .min(2, 'Service type must be at least 2 characters')
    .max(50, 'Service type cannot exceed 50 characters')
    .typeError('Must be a valid text'),
  language: Yup.string()
    .required('Language is required')
    .oneOf(['en', 'fi', 'sv', 'de'], 'Invalid language')
    .typeError('Must be a valid language'),
  source_language: Yup.string()
    .required('Source language is required')
    .oneOf(['en', 'fi', 'sv', 'de'], 'Invalid source language')
    .typeError('Must be a valid source language'),
  translations: Yup.object().notRequired().nullable(),
  media: Yup.array().notRequired().nullable(),
});
